import React, { useState, useEffect, useRef, useCallback } from 'react';

import { mount, unmount, setChildren } from 'redom';

import { Node } from 'slate';
import { ReactEditor, useSlateStatic } from 'slate-react';
import { HistoryEditor } from 'slate-history';

import useOnClickOutside from 'generics/hooks/on-click-outside';
import useOnEscapeKey from 'generics/hooks/on-escape-key';

import HelptapEditor from '../../helpers';

import BaseElement from '../base';

import NativeCanvas from './canvas';
import {
  waitForResources
} from './utilities';

import './index.scss';

function Canvas(props) {

  const element = props.element;

  const editor = useSlateStatic();

  const elementPath = ReactEditor.findPath(editor, props.element);
  const mainReference = useRef(null);
  const unloadedReference = useRef(false);
  const nativeCanvasReference = useRef(null);
  const containerReference = useRef(null);

  const [rootState, setRootState] = useState({
    loadedPercent: 0
  });

  useOnClickOutside(containerReference, tryUnselectActiveObject);
  useOnEscapeKey(tryUnselectActiveObject);

  function setLoadedPercent(loadedPercent) {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        loadedPercent
      };

    });

  }

  function tryUnselectActiveObject() {
    const canvas = nativeCanvasReference.current;
    if (canvas === null) {
      return;
    }
    canvas.deselectAll();
  }

  const updateProperties = useCallback(function updateProperties(properties) {

    const updatedNode = Node.get(editor, elementPath);

    HelptapEditor.setAttributes(editor, {
      properties: {
        ...updatedNode.properties,
        ...properties
      }
    }, elementPath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function onLoad() {

    return initialize();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function onPropertiesChange() {

    if (nativeCanvasReference.current === null) {
      return;
    }

    nativeCanvasReference.current.update(structuredClone(element), props.mediaUploader);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element.width, element.height, element.backgroundColor, element.properties]);

  useEffect(() => {
    // eslint-disable-next-line func-style
    const handleKeydown = (event) => {

      const isUndo = (event.metaKey || event.ctrlKey) && event.key === 'z';
      const isRedo = (event.metaKey || event.ctrlKey) && event.shiftKey && event.key === 'Z';

      if (isUndo || isRedo) {
        event.preventDefault();
        if (isRedo) {
          HistoryEditor.redo(editor);
        } else if (isUndo) {
          HistoryEditor.undo(editor);
        }
      }

    };

    // Attach the event listener to the Fabric.js canvas container
    const canvasEl = containerReference.current;
    canvasEl.addEventListener('keydown', handleKeydown);

    // Ensure Fabric.js canvas is focusable
    canvasEl.tabIndex = 0;

    return () => {
      canvasEl.removeEventListener('keydown', handleKeydown);
    };
  }, [editor]);

  function initialize() {

    const container = containerReference.current;

    const loader = waitForResources(element.properties.data);

    // eslint-disable-next-line no-console
    loader.on('progress', (progress) => setLoadedPercent(progress));

    loader.on('done', () => {

      if (unloadedReference.current === true) {
        return;
      }

      const canvas = new NativeCanvas(element, props.mediaUploader, updateProperties, 'image');
      nativeCanvasReference.current = canvas;

      container.canvas = nativeCanvasReference.current;

      setChildren(container, [nativeCanvasReference.current]);
    });

    return uninitialize.bind(null, container);

  }

  function uninitialize(container) {

    unloadedReference.current = true;

    if (nativeCanvasReference.current === null) {
      return;
    }

    nativeCanvasReference.current.onunmount();

  }

  function renderContent() {

    const mainAttributes = {
      ...props.attributes,
      contentEditable: false,
      className: 'canvas-content'
    };

    const containerAttributes = {
      className: 'canvas-container',
      ref: containerReference,
      tabIndex: 0,
      onKeyDown(event) {
        // if command + d or ctrl + d is pressed, duplicate the active object
        if ((event.metaKey || event.ctrlKey) && event.key === 'd') {
          event.preventDefault();
          event.stopPropagation();
          nativeCanvasReference.current.duplicateActiveObject();
        }
      },
      onMouseLeave() {
        const canvas = nativeCanvasReference.current;
        if (canvas === null) {
          return;
        }
        // canvas.deselectAll();
      },
      onMouseEnter() {
        const canvas = nativeCanvasReference.current;
        if (canvas === null) {
          return;
        }
        // canvas.selectLastActiveObject();
      }
    };

    return <div {...mainAttributes}>
      <div {...containerAttributes}>
        <div className="canvas-loader-display">
          <ht-spinner size="small" />
          <div className="canvas-loader-text sub-text">{rootState.loadedPercent}%</div>
        </div>
      </div>
      {props.children}
    </div>;

  }

  function renderMain() {

    const baseElementProperties = {
      ...props,
      placeholder: '',
      className: 'canvas-main'
    };

    return <BaseElement {...baseElementProperties}>
      {renderContent()}
    </BaseElement>;

  }

  return renderMain();

}

export default Canvas;